import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import type { TeamMember, OrgRole } from '@/types/team-members'
import {
  ASSIGNABLE_ROLES,
  ORG_ROLE_LABELS,
  ORG_ROLE_DESCRIPTIONS,
} from '@/types/team-members'

interface EditMemberRoleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  member: TeamMember | null
  onUpdateRole: (newRole: OrgRole) => Promise<void>
  isLoading?: boolean
}

export function EditMemberRoleDialog({
  open,
  onOpenChange,
  member,
  onUpdateRole,
  isLoading = false,
}: EditMemberRoleDialogProps) {
  const [role, setRole] = useState<OrgRole>(
    member?.role || 'member'
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Update role when member changes
  if (member && role !== member.role) {
    setRole(member.role)
  }

  const hasChanged = role !== member?.role

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!hasChanged || !member) return

    setIsSubmitting(true)
    try {
      await onUpdateRole(role)
      onOpenChange(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!member) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Member Role</DialogTitle>
          <DialogDescription>
            Change the role for {member.display_name || member.email}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Member</Label>
            <div className="flex flex-col gap-1">
              <p className="font-medium text-sm">{member.display_name}</p>
              <p className="text-xs text-muted-foreground">{member.email}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">New Role</Label>
            <Select
              value={role}
              onValueChange={(value) => setRole(value as OrgRole)}
              disabled={isSubmitting || isLoading}
            >
              <SelectTrigger id="role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ASSIGNABLE_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ORG_ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {ORG_ROLE_DESCRIPTIONS[role]}
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting || isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!hasChanged || isSubmitting || isLoading}
            >
              {isSubmitting ? 'Updating...' : 'Update Role'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
